import { screenPoweredOn, type PlayerManifest } from '@tbcast/shared';
import type { NativeBridge } from '@tbcast/shared/native';
import { io, type Socket } from 'socket.io-client';
import { ApiError, PlayerApi, type QueuedCommand } from './api';
import { AssetCache, freeStorageBytes, requestPersistentStorage } from './cache';
import { PLAYER_VERSION, readConfig, type PlayerConfig } from './config';
import { RootPlayer } from './engine/root';
import type { PlaybackContext } from './engine/types';
import { browserLocale, t } from './i18n';
import { getNative, platformName } from './native';
import { captureScreenshot } from './screenshot';
import * as store from './store';
import './styles.css';
import {
  installFullscreenControls,
  renderEmergency,
  renderMessage,
  renderPairing,
  setOfflineBadge,
  setSyncBadge,
  showIdentify,
} from './ui';
import { createWidgetRenderable } from './widgets';
import { lastKnownTemp, refreshTemp } from './widgets/weather';

type PopEvent = {
  contentKind: string;
  contentId: string;
  contentName?: string;
  startedAt: string;
  durationMs: number;
};

const MAX_POP_BUFFER = 5000;

class Player {
  private cfg: PlayerConfig;
  private api: PlayerApi;
  private cache: AssetCache;
  private native: NativeBridge | null = null;
  private manifest: PlayerManifest | null = null;
  private etag: string | null = null;
  private root: RootPlayer | null = null;
  private socket: Socket | null = null;
  private pop: PopEvent[] = [];
  private executed: string[] = [];
  private timers: Array<ReturnType<typeof setInterval>> = [];
  private errors: number[] = [];
  private online = navigator.onLine;
  private displayOn = true;
  private startedAt = Date.now();
  private app: HTMLElement;
  private stage: HTMLElement;
  private overlay: HTMLElement;
  private locale: 'nl' | 'en' = browserLocale();
  /** Stats for monitoring and automated soak tests. */
  stats = { manifestLoads: 0, transitions: 0, errors: 0, itemsStarted: 0, lastItemAt: 0 };

  constructor() {
    this.cfg = readConfig();
    this.api = new PlayerApi(this.cfg);
    this.cache = new AssetCache(!this.cfg.previewScreenId);
    this.app = document.getElementById('app')!;
    this.app.innerHTML =
      '<div class="tb-viewport"><div class="tb-stage" data-testid="stage"></div><div class="tb-overlay"></div></div>';
    this.stage = this.app.querySelector('.tb-stage')!;
    this.overlay = this.app.querySelector('.tb-overlay')!;
    (window as unknown as { __tbcast: Player }).__tbcast = this;
  }

  async boot(): Promise<void> {
    this.installWatchdog();
    installFullscreenControls();
    window.addEventListener('online', () => this.setOnline(true));
    window.addEventListener('offline', () => this.setOnline(false));
    await this.registerServiceWorker();
    void requestPersistentStorage();
    this.native = await getNative(this.cfg);
    this.pop = (await store.get<PopEvent[]>(store.KEYS.pop)) ?? [];
    this.executed = (await store.get<string[]>(store.KEYS.executed)) ?? [];

    if (this.cfg.widgetPreview) {
      document.body.classList.add('tb-preview');
      const wp = this.cfg.widgetPreview;
      const fake: PlayerManifest = {
        version: 'preview',
        generatedAt: new Date().toISOString(),
        screen: {
          id: 'preview',
          name: 'Preview',
          orientation: 'landscape',
          timezone: wp.timeZone,
          volume: 0,
          powerOnTime: null,
          powerOffTime: null,
          location: null,
          tags: [],
        },
        organization: { id: 'preview', name: 'Preview', locale: wp.locale },
        license: { allowed: true },
        root: { kind: 'media', id: 'w' },
        emergency: null,
        assets: [],
        media: [],
        presentations: [],
        widgets: [{ id: 'w', name: 'preview', type: wp.type, config: wp.config }],
        playlists: [],
        layouts: [],
        schedules: [],
        config: { heartbeatSec: 30, pollSec: 60, screenshotOnChange: false },
      };
      this.locale = wp.locale;
      const r = createWidgetRenderable(wp.type, wp.config, this.context(fake));
      r.el.style.visibility = 'visible';
      this.stage.replaceChildren(r.el);
      await r.ready().catch(() => undefined);
      r.play();
      return;
    }

    if (this.cfg.demo || this.cfg.previewScreenId) {
      document.body.classList.add(this.cfg.demo ? 'tb-demo' : 'tb-preview');
      await this.refreshManifest();
      this.timers.push(setInterval(() => void this.refreshManifest(), 10 * 60_000));
      return;
    }

    let token = this.cfg.pathToken;
    if (token) {
      await this.saveToken(token);
      history.replaceState(null, '', '/');
    } else {
      token =
        (await this.native?.getToken().catch(() => null)) ??
        (await store.get<string>(store.KEYS.token)) ??
        null;
    }
    if (!token) return this.pair();
    await this.start(token);
  }

  private async registerServiceWorker(): Promise<void> {
    // The SW is emitted by the production build only.
    if (!('serviceWorker' in navigator) || location.protocol === 'file:' || import.meta.env.DEV)
      return;
    try {
      await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      this.cache.setServiceWorkerActive(!!navigator.serviceWorker.controller);
      navigator.serviceWorker.addEventListener('controllerchange', () =>
        this.cache.setServiceWorkerActive(true),
      );
    } catch {
      // No SW (e.g. insecure context on a LAN IP): play straight from the network.
    }
  }

  private async saveToken(token: string | null): Promise<void> {
    if (token) await store.set(store.KEYS.token, token);
    else await store.del(store.KEYS.token);
    await this.native?.setToken(token).catch(() => undefined);
  }

  // ───────────────────────── Pairing ─────────────────────────

  private async pair(): Promise<void> {
    this.stopAll();
    const platform = platformName(this.native);
    const appHost = this.cfg.apiUrl.replace(/^https?:\/\//, '') + '/app/screens';
    for (;;) {
      let session: { code: string; secret: string; expiresAt: string; pairUrl: string };
      try {
        const saved = await store.get<typeof session>(store.KEYS.pairing);
        session =
          saved && new Date(saved.expiresAt).getTime() - Date.now() > 60_000
            ? saved
            : await this.api.startPairing(platform);
        await store.set(store.KEYS.pairing, session);
      } catch {
        renderMessage(this.stage, { title: t(this.locale, 'pairOffline'), kind: 'error' });
        await sleep(10_000);
        continue;
      }
      await renderPairing(this.stage, { ...session, locale: this.locale, appHost });
      while (new Date(session.expiresAt).getTime() > Date.now()) {
        await sleep(3000);
        try {
          const st = await this.api.pairingStatus(session.code, session.secret);
          if (st.status === 'paired' && st.token) {
            await store.del(store.KEYS.pairing);
            await this.saveToken(st.token);
            renderMessage(this.stage, { title: t(this.locale, 'pairPaired') });
            return this.start(st.token);
          }
          if (st.status === 'expired' || st.status === 'unknown') break;
        } catch {
          // keep polling
        }
      }
      await store.del(store.KEYS.pairing);
    }
  }

  // ───────────────────────── Running ─────────────────────────

  private async start(token: string): Promise<void> {
    this.api.token = token;
    const cached = await store.get<PlayerManifest>(store.KEYS.manifest);
    this.etag = (await store.get<string>(store.KEYS.manifestEtag)) ?? null;
    if (cached) this.apply(cached);
    else renderMessage(this.stage, { title: t(this.locale, 'loading') });
    await this.refreshManifest(!cached);
    this.connectRealtime(token);
    this.timers.push(
      setInterval(() => void this.heartbeat(), 30_000),
      setInterval(() => void this.refreshManifest(), 60_000),
      setInterval(() => void this.flushPop(), 60_000),
      setInterval(() => this.checkPower(), 30_000),
      setInterval(() => this.checkHealth(), 5 * 60_000),
    );
    void this.heartbeat();
  }

  private connectRealtime(token: string): void {
    this.socket = io(this.cfg.realtimeUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnectionDelayMax: 30_000,
    });
    this.socket.on('manifest-changed', () => void this.refreshManifest(true));
    this.socket.on('command', (cmd: QueuedCommand) => void this.execute(cmd));
    this.socket.on('connect', () => void this.refreshManifest(true));
    this.socket.on('connect_error', (err) => {
      if (err.message === 'unauthorized') void this.unpair();
    });
  }

  private async refreshManifest(force = false): Promise<void> {
    try {
      const res = await this.api.manifest(force ? null : this.etag);
      this.setOnline(true);
      if (!res) return;
      this.stats.manifestLoads++;
      if (this.manifest && res.manifest.version === this.manifest.version && !force) return;
      if (this.manifest && res.manifest.version === this.manifest.version) {
        // Same content, fresh signed URLs: just re-sync assets.
        void this.syncAssets(res.manifest);
        return;
      }
      this.etag = res.etag;
      if (!this.cfg.demo && !this.cfg.previewScreenId) {
        await store.set(store.KEYS.manifest, res.manifest);
        if (res.etag) await store.set(store.KEYS.manifestEtag, res.etag);
      }
      this.apply(res.manifest);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return this.unpair();
      this.setOnline(false);
    }
  }

  private async unpair(): Promise<void> {
    this.etag = null;
    await store.del(store.KEYS.manifest);
    await store.del(store.KEYS.manifestEtag);
    await this.saveToken(null);
    this.root?.destroy();
    this.root = null;
    this.manifest = null;
    await this.pair();
  }

  private async syncAssets(m: PlayerManifest): Promise<void> {
    this.cache.onProgress = (p) =>
      setSyncBadge(
        p.done < p.total ? t(this.locale, 'syncing', { done: p.done, total: p.total }) : null,
      );
    await this.cache.sync(m.assets);
    setSyncBadge(null);
  }

  private apply(m: PlayerManifest): void {
    this.manifest = m;
    this.locale = m.organization.locale === 'en' ? 'en' : 'nl';
    document.documentElement.lang = this.locale;
    this.app.dataset.orientation = m.screen.orientation;
    renderEmergency(this.overlay, m.emergency);

    if (!m.license.allowed) {
      this.root?.destroy();
      this.root = null;
      renderMessage(this.stage, {
        title: t(this.locale, 'licenseTitle'),
        text:
          m.license.reason === 'unlicensed'
            ? t(this.locale, 'licenseUnlicensed')
            : t(this.locale, 'licenseText'),
        kind: 'license',
        testId: 'license-expired',
      });
      return;
    }

    void this.syncAssets(m).then(() => {
      // Once cached, newly created items play from the local cache.
    });
    if (m.screen.location)
      void refreshTemp(m.screen.location.lat, m.screen.location.lng, m.screen.timezone);

    const ctx = this.context(m);
    if (!m.root) {
      this.root?.destroy();
      this.root = null;
      renderMessage(this.stage, {
        title: t(this.locale, 'noContent'),
        text: t(this.locale, 'noContentText'),
        testId: 'no-content',
      });
      return;
    }
    if (this.root) this.root.update(ctx);
    else {
      this.stage.replaceChildren();
      this.root = new RootPlayer(this.stage, ctx);
      this.root.start();
    }
    this.checkPower();
  }

  private context(m: PlayerManifest): PlaybackContext {
    return {
      manifest: m,
      cache: this.cache,
      api: this.api,
      timeZone: m.screen.timezone,
      locale: this.locale,
      speed: this.cfg.speed,
      volume: m.screen.volume,
      weatherTemp: lastKnownTemp,
      log: (msg, data) => {
        if (this.cfg.debug) console.info('[tbcast]', msg, data ?? '');
      },
      onItemStart: (info) => {
        const started = Date.now();
        this.stats.itemsStarted++;
        this.stats.transitions++;
        this.stats.lastItemAt = started;
        return () => {
          if (this.cfg.demo || this.cfg.previewScreenId) return;
          this.pop.push({
            contentKind: info.kind,
            contentId: info.id,
            contentName: info.name,
            startedAt: new Date(started).toISOString(),
            durationMs: Date.now() - started,
          });
          if (this.pop.length > MAX_POP_BUFFER)
            this.pop.splice(0, this.pop.length - MAX_POP_BUFFER);
        };
      },
    };
  }

  // ───────────────────────── Telemetry ─────────────────────────

  private async heartbeat(): Promise<void> {
    if (!this.manifest) return;
    const native = await this.native?.telemetry().catch(() => null);
    const mem = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
    try {
      const res = await this.api.heartbeat({
        playerVersion: `${PLAYER_VERSION}${this.native ? `+${this.native.version}` : ''}`.slice(
          0,
          40,
        ),
        platform: platformName(this.native).slice(0, 40),
        resolution:
          `${window.screen.width * devicePixelRatio}x${window.screen.height * devicePixelRatio}`.slice(
            0,
            20,
          ),
        freeStorageBytes: native?.freeStorageBytes ?? (await freeStorageBytes()),
        cpuPercent: native?.cpuPercent,
        temperatureC: native?.temperatureC,
        memoryMb: native?.memoryMb ?? (mem ? Math.round(mem.usedJSHeapSize / 1048576) : undefined),
        currentContent: this.root?.currentRef ?? undefined,
        manifestVersion: this.manifest.version,
      });
      this.setOnline(true);
      for (const c of res.commands) await this.execute(c);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return this.unpair();
      this.setOnline(false);
    }
  }

  private async flushPop(): Promise<void> {
    if (!this.pop.length || !this.api.token) return;
    const batch = this.pop.slice(0, 500);
    try {
      await this.api.proofOfPlay(batch);
      this.pop.splice(0, batch.length);
    } catch {
      // keep buffered (also across reboots)
    }
    await store.set(store.KEYS.pop, this.pop);
  }

  private setOnline(online: boolean): void {
    if (this.online === online) return;
    this.online = online;
    setOfflineBadge(!online, t(this.locale, 'offline'));
  }

  // ───────────────────────── Commands ─────────────────────────

  private async execute(cmd: QueuedCommand): Promise<void> {
    if (!cmd?.id || this.executed.includes(cmd.id)) return;
    this.executed = [...this.executed, cmd.id].slice(-50);
    await store.set(store.KEYS.executed, this.executed);
    this.socket?.emit('ack', { commandId: cmd.id });
    switch (cmd.command) {
      case 'reload':
        location.reload();
        break;
      case 'clear-cache':
        await this.cache.clear();
        await store.clearAllExcept([store.KEYS.token, store.KEYS.executed]);
        if ('caches' in window) for (const k of await caches.keys()) await caches.delete(k);
        location.reload();
        break;
      case 'screenshot': {
        const blob = await captureScreenshot(this.native, this.app);
        if (blob) await this.api.screenshot(blob).catch(() => undefined);
        break;
      }
      case 'identify':
        showIdentify(this.manifest?.screen.name ?? '');
        break;
      case 'display-off':
        await this.setDisplay(false, true);
        break;
      case 'display-on':
        await this.setDisplay(true, true);
        break;
      case 'reboot':
        if (!(await this.native?.reboot().catch(() => false))) location.reload();
        break;
      case 'update': {
        const ok = await this.native?.update().catch(() => false);
        if (!ok) {
          const reg = await navigator.serviceWorker?.getRegistration();
          await reg?.update().catch(() => undefined);
          location.reload();
        }
        break;
      }
    }
  }

  // ───────────────────────── Power schedule ─────────────────────────

  private manualDisplay: boolean | null = null;

  private async setDisplay(on: boolean, manual = false): Promise<void> {
    if (manual) this.manualDisplay = on;
    if (this.displayOn === on) return;
    this.displayOn = on;
    document.body.classList.toggle('tb-display-off', !on);
    await this.native?.setDisplay(on).catch(() => false);
  }

  private checkPower(): void {
    const s = this.manifest?.screen;
    if (!s) return;
    const scheduled = screenPoweredOn(s.powerOnTime, s.powerOffTime, new Date(), s.timezone);
    // A manual command wins until the schedule changes state.
    if (this.manualDisplay !== null && this.manualDisplay !== scheduled) return;
    this.manualDisplay = null;
    void this.setDisplay(scheduled);
  }

  // ───────────────────────── Watchdog ─────────────────────────

  private installWatchdog(): void {
    const onError = () => {
      this.stats.errors++;
      const now = Date.now();
      this.errors = [...this.errors.filter((t) => now - t < 5 * 60_000), now];
      if (this.errors.length > 20) location.reload();
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onError);
  }

  private checkHealth(): void {
    const mem = (
      performance as unknown as { memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number } }
    ).memory;
    if (mem && mem.usedJSHeapSize > mem.jsHeapSizeLimit * 0.85) location.reload();
    // Nothing started for 30 minutes while content is assigned: the renderer is stuck.
    if (
      this.manifest?.root &&
      this.stats.lastItemAt &&
      Date.now() - this.stats.lastItemAt > 30 * 60_000 &&
      this.root?.currentRef?.startsWith('playlist')
    )
      location.reload();
    // Daily refresh at night keeps long-running browsers fresh.
    const hour = new Date().getHours();
    if (hour === 4 && Date.now() - this.startedAt > 12 * 3600_000) location.reload();
  }

  private stopAll(): void {
    for (const t of this.timers) clearInterval(t);
    this.timers = [];
    this.socket?.disconnect();
    this.socket = null;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

void new Player().boot();
